/**
 * CronosLottoPromoPopup Component
 *
 * Promotional slide-in popup for Cronos Lotto feature
 * Shows potential gains from delegating and participating in the lottery
 * Slides in from the right, can be dismissed, remembers user preference
 * Includes email input for interest registration (similar to HostingPromoPopup)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';

interface CronosLottoPromoPopupProps {
  wallets?: string[];
  totalStaked?: number;
  croPrice?: number;
}

const POPUP_DISMISSED_KEY = 'cronos_lotto_promo_dismissed';
const POPUP_DISMISS_DURATION = 3 * 24 * 60 * 60 * 1000; // 3 days

// Lotto configuration (should match CronosMonitor)
const LOTTO_CONFIG = {
  dailyPrizePercent: 30,
  winnersPerDay: 50,
  minStake: 10000,
  apy: 7.44,
};

export function CronosLottoPromoPopup({ wallets = [], totalStaked = 0, croPrice = 0.10 }: CronosLottoPromoPopupProps) {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Check if popup was dismissed recently
    const dismissedAt = localStorage.getItem(POPUP_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < POPUP_DISMISS_DURATION) {
        return; // Still within dismiss period
      }
    }

    // Show popup after a delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 4000); // Show after 4 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem(POPUP_DISMISSED_KEY, Date.now().toString());
    }, 300);
  };

  const handleSubmit = async () => {
    if (!email || isSubmitting) return;

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/cronos/lotto-interest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          wallets,
          totalStaked,
          croPrice,
          language: i18n.language,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Auto-close after success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending lotto interest:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '340px',
        background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.98), rgba(20, 20, 30, 0.98))',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        zIndex: 10001, // Above support chat (9999)
        overflow: 'hidden',
        animation: isClosing
          ? 'lottoSlideOutRight 0.3s ease-in forwards'
          : 'lottoSlideInRight 0.4s ease-out',
      }}
    >
      {/* Gradient accent bar - Gold theme */}
      <div
        style={{
          height: '4px',
          background: 'linear-gradient(90deg, #FFD700, #FFA500, #FF8C00)',
        }}
      />

      {/* Header */}
      <div style={{ padding: '16px 16px 12px', position: 'relative' }}>
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '8px',
            padding: '6px',
            cursor: 'pointer',
            color: 'var(--muted)',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'var(--muted)';
          }}
          aria-label={t('common.close')}
        >
          <Icons.X size={16} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          {/* Loaded Lion mini icon */}
          <img
            src="/loaded-lion.svg"
            alt="Loaded Lion"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              boxShadow: '0 2px 10px rgba(255, 215, 0, 0.3)',
            }}
          />
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              {t('cronos.lottoPromo.title', 'Cronos Lotto')}
            </h4>
            <p style={{ fontSize: '11px', color: '#FFD700', margin: 0, fontWeight: 600 }}>
              {t('cronos.lottoPromo.subtitle', 'Loterie sans perte')}
            </p>
          </div>
        </div>

        {/* Catchy tagline */}
        <p style={{
          fontSize: '13px',
          color: 'var(--text)',
          margin: '10px 0 0',
          lineHeight: 1.4,
        }}>
          {t('cronos.lottoPromo.tagline', 'Stakez vos CRO et gagnez des récompenses bonus chaque jour !')}
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {/* Daily winners */}
          <div
            style={{
              flex: 1,
              background: 'rgba(16, 185, 129, 0.15)',
              borderRadius: '10px',
              padding: '12px 8px',
              textAlign: 'center',
              border: '1px solid rgba(16, 185, 129, 0.3)',
            }}
          >
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#10b981' }}>
              {LOTTO_CONFIG.winnersPerDay}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('cronos.lottoPromo.winnersDaily', 'gagnants/jour')}
            </div>
          </div>

          {/* Prize pool */}
          <div
            style={{
              flex: 1,
              background: 'rgba(255, 215, 0, 0.15)',
              borderRadius: '10px',
              padding: '12px 8px',
              textAlign: 'center',
              border: '1px solid rgba(255, 215, 0, 0.3)',
            }}
          >
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#FFD700' }}>
              {LOTTO_CONFIG.dailyPrizePercent}%
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('cronos.lottoPromo.ofDailyGains', 'des gains distribués')}
            </div>
          </div>

          {/* APY */}
          <div
            style={{
              flex: 1,
              background: 'rgba(102, 126, 234, 0.15)',
              borderRadius: '10px',
              padding: '12px 8px',
              textAlign: 'center',
              border: '1px solid rgba(102, 126, 234, 0.3)',
            }}
          >
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#667eea' }}>
              {LOTTO_CONFIG.apy}%
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              APY staking
            </div>
          </div>
        </div>

        {/* Key features */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(255, 215, 0, 0.15)',
            color: '#FFD700',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Check size={10} />
            {t('cronos.lottoPromo.noLoss', 'Sans perte')}
          </span>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(16, 185, 129, 0.15)',
            color: '#10b981',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Check size={10} />
            {t('cronos.lottoPromo.dailyDraw', 'Tirage quotidien')}
          </span>
          <span style={{
            fontSize: '10px',
            padding: '4px 8px',
            background: 'rgba(102, 126, 234, 0.15)',
            color: '#667eea',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Icons.Check size={10} />
            {t('cronos.lottoPromo.autoEntry', 'Participation auto')}
          </span>
        </div>

        {/* Minimum stake info */}
        <div style={{
          padding: '10px 12px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          marginBottom: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Icons.Info size={14} color="var(--muted)" />
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
            {t('cronos.lottoPromo.minStake', 'Minimum {{amount}} CRO pour participer', { amount: LOTTO_CONFIG.minStake.toLocaleString() })}
          </span>
        </div>

        {/* Email Input & Submit */}
        {submitStatus === 'success' ? (
          <div style={{
            padding: '14px',
            background: 'rgba(16, 185, 129, 0.15)',
            borderRadius: '10px',
            textAlign: 'center',
            color: '#10b981',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}>
            <Icons.Check size={18} />
            {t('cronos.lottoPromo.interestSent', 'Intérêt enregistré !')}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('cronos.lottoPromo.emailPlaceholder', 'Votre email')}
              style={{
                flex: 1,
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: submitStatus === 'error' ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: 'var(--text)',
                fontSize: '13px',
                outline: 'none',
                transition: 'all 0.2s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#FFD700';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              disabled={isSubmitting}
            />
            <button
              onClick={handleSubmit}
              disabled={!email || isSubmitting}
              style={{
                padding: '12px 16px',
                background: (!email || isSubmitting)
                  ? 'rgba(255, 215, 0, 0.3)'
                  : 'linear-gradient(135deg, #FFD700, #FFA500)',
                border: 'none',
                borderRadius: '10px',
                color: '#1a1a2e',
                fontSize: '13px',
                fontWeight: 600,
                cursor: (!email || isSubmitting) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                opacity: (!email || isSubmitting) ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (email && !isSubmitting) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 215, 0, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title={t('cronos.lottoPromo.notifyMe', 'Me notifier')}
            >
              {isSubmitting ? (
                <Icons.Activity size={18} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Icons.Send size={18} />
              )}
            </button>
          </div>
        )}

        {/* Wallet info hint */}
        {wallets.length > 0 && submitStatus !== 'success' && (
          <p style={{
            marginTop: '8px',
            fontSize: '10px',
            color: 'var(--muted)',
            textAlign: 'center',
          }}>
            {t('cronos.lottoPromo.walletsIncluded', '{{count}} wallet(s) seront inclus', { count: wallets.length })}
          </p>
        )}

        {/* Current stake hint if user has some */}
        {totalStaked > 0 && submitStatus !== 'success' && (
          <p style={{
            marginTop: '8px',
            fontSize: '11px',
            color: totalStaked >= LOTTO_CONFIG.minStake ? '#10b981' : 'var(--muted)',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
          }}>
            {totalStaked >= LOTTO_CONFIG.minStake ? (
              <>
                <Icons.Check size={12} />
                {t('cronos.lottoPromo.eligible', 'Vous êtes éligible avec {{amount}} CRO', { amount: totalStaked.toLocaleString() })}
              </>
            ) : (
              <>
                {t('cronos.lottoPromo.needMore', 'Il vous manque {{amount}} CRO', { amount: (LOTTO_CONFIG.minStake - totalStaked).toLocaleString() })}
              </>
            )}
          </p>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes lottoSlideInRight {
          from {
            transform: translateX(120%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes lottoSlideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(120%);
            opacity: 0;
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
